import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import Dialog from "components/shared/Dialog";
import Button from "components/shared/Button";
import SectionDetails from "../../../../Component/components/drawers/ShiftTrade/SectionDetails";
import UserSelect from "./UserSelect";
import { calculateSections } from "../../../../Component/hooks/form/useCalculatedSections";
import { withLoadingToast } from "../../../../Component/helpers/withLoadingToast";
import useApi from "utils/api";
import { getErrorMessage } from "utils/helpers/errors";
import type { CalculatedSection } from "../../../../Component/components/drawers/ShiftTrade/types";

interface AssignDialogProps {
    visible: boolean;
    close: () => void;
    request: any;
    queryOptions: { queryKey: QueryKey };
    onSuccess?: () => void;
}

function AssignDialog({ visible, close, request, queryOptions, onSuccess }: AssignDialogProps) {
    const { t } = useTranslation();
    const { authPut } = useApi();
    const queryClient = useQueryClient();
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);

    const schedule = request?.data?.schedule;

    const sections = useMemo((): CalculatedSection[] => {
        return calculateSections(schedule?.sections, schedule?.startDate) ?? [];
    }, [schedule]);

    const onUsersChange = useCallback((users: any[]) => {
        setSelectedUsers(users);
        setError(null);
    }, []);

    const onClose = useCallback(() => {
        setSelectedUsers([]);
        setError(null);
        close();
    }, [close]);

    const { mutate, isPending } = useMutation({
        ...withLoadingToast(t("assigning")),
        mutationFn: (userIds: string[]) => authPut(`/request/${request?.id}/assign-open-shift`, {
            data: { userIds },
        }),
        onSuccess: () => {
            toast.success(t("shift-assigned-successfully"));
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            onSuccess?.();
            onClose();
        },
        onError: (err) => getErrorMessage(err, t),
    });

    const onSubmit = useCallback(() => {
        if (!selectedUsers.length) {
            setError(t("select-at-least-one-user"));
            return;
        }
        mutate(selectedUsers.map((user) => user.id));
    }, [selectedUsers, mutate, t]);

    return (
        <Dialog
            open={visible}
            close={onClose}
            title={t("assign-to-user")}
            size="2xl"
            footer={(
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
                        {t("cancel")}
                    </Button>
                    <Button
                        type="button"
                        icon={<UserPlus />}
                        loading={isPending}
                        disabled={isPending || !selectedUsers.length}
                        onClick={onSubmit}
                    >
                        {t("assign")}
                    </Button>
                </div>
            )}
        >
            {sections.length > 0 && (
                <>
                    <SectionDetails sections={sections} keepZone />
                    <UserSelect
                        sections={sections}
                        shiftId={schedule?.id}
                        value={selectedUsers}
                        onChange={onUsersChange}
                        error={error}
                    />
                </>
            )}
        </Dialog>
    );
}

export default AssignDialog;
